import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { currencyGainToast } from "@/lib/currencyToastGate";
import confetti from "canvas-confetti";

import api, { apiErr } from "@/lib/api";
import Modal from "@/components/Modal";
import { Empty, Loader, Pagination, btn } from "@/components/Page";

const CONSUMABLE_ASSETS = {
  campaign_stamina_potion: "/campaign-stamina.png",
  instant_scout: "/instant-scout.png",
  experienced_medicus: "/medicus.png",
  common_weapon_chest: "/common-chest.png",
  common_armor_chest: "/common-chest.png",
  uncommon_weapon_chest: "/uncommon-chest.png",
  uncommon_armor_chest: "/uncommon-chest.png",
  rare_weapon_chest: "/rare-chest.png",
  rare_armor_chest: "/rare-chest.png",
};

const RARITY_COLORS = {
  common: "#e4e4e7",
  uncommon: "#22c55e",
  rare: "#3b82f6",
  elite: "#a855f7",
  legendary: "#f97316",
};

function fragmentPhrase(kind) {
  return kind === "armor" ? "Armor Frags" : "Weapon Frags";
}

function tinyWheelLabel(entry) {
  const rewardType = entry?.type || entry?.reward_type;
  const amount = Number(entry?.amount || entry?.reward_amount || 0);
  if (rewardType === "gold") return `${amount}G`;
  if (rewardType === "follis") return `${amount}F`;
  if (rewardType === "fragment") {
    const kind = String(entry?.fragment_kind || "weapon").toLowerCase();
    return `${amount}${kind === "armor" ? "AF" : "WF"}`;
  }
  const tinyConsumable = {
    campaign_stamina_potion: "Stamina",
    instant_scout: "Scout",
    experienced_medicus: "Medicus",
    common_weapon_chest: "C-Weap",
    common_armor_chest: "C-Armor",
    uncommon_weapon_chest: "U-Weap",
    uncommon_armor_chest: "U-Armor",
    rare_weapon_chest: "R-Weap",
    rare_armor_chest: "R-Armor",
  };
  return tinyConsumable[String(entry?.consumable_id || "")] || "Item";
}

function formatDateTime(iso) {
  if (!iso) return "Unknown time";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function effectiveSliceColor(entry) {
  const rewardType = entry?.type || entry?.reward_type;
  if (rewardType === "fragment") {
    const rarity = String(entry?.fragment_rarity || "common").toLowerCase();
    return RARITY_COLORS[rarity] || RARITY_COLORS.common;
  }
  return entry?.color || "#b45309";
}

function buildSliceGeometry(entries) {
  const total = entries.reduce((sum, entry) => sum + Number(entry.weight || 0), 0) || 1;
  let cursor = 0;
  return entries.map((entry) => {
    const sweep = (Number(entry.weight || 0) / total) * 360;
    const start = cursor;
    cursor += sweep;
    return {
      ...entry,
      color: effectiveSliceColor(entry),
      start,
      sweep,
      mid: start + (sweep / 2),
    };
  });
}

function wheelGradient(entries) {
  const slices = buildSliceGeometry(entries);
  const stops = slices.map((slice) => `${slice.color} ${slice.start}deg ${slice.start + slice.sweep}deg`);
  return `conic-gradient(${stops.join(", ")})`;
}

function historyLabel(row) {
  return row?.reward_label || row?.reward_payload?.label || "Reward won";
}

function rewardVisual(entry) {
  const rewardType = entry?.type || entry?.reward_type;
  const amount = Number(entry?.amount || entry?.reward_amount || entry?.reward_payload?.amount || 0);
  if (rewardType === "gold") {
    return { kind: "image", src: "/gold-coin.png", alt: "Gold", shortLabel: `${amount} Gold` };
  }
  if (rewardType === "follis") {
    return { kind: "image", src: "/follis-coin.png", alt: "Follis", shortLabel: `${amount} Follis` };
  }
  if (rewardType === "fragment") {
    const rarity = String(entry?.fragment_rarity || entry?.reward_payload?.fragment_rarity || "common").toLowerCase();
    const kind = String(entry?.fragment_kind || entry?.reward_payload?.fragment_kind || "weapon").toLowerCase();
    return {
      kind: "swatch",
      color: RARITY_COLORS[rarity] || RARITY_COLORS.common,
      alt: `${rarity} ${kind} fragments`,
      shortLabel: `${amount} ${fragmentPhrase(kind)}`,
    };
  }
  const consumableId = String(entry?.consumable_id || entry?.reward_payload?.consumable_id || "campaign_stamina_potion");
  const shortLabels = {
    campaign_stamina_potion: "Stamina Potion",
    instant_scout: "Instant Scout",
    experienced_medicus: "Medicus",
    common_weapon_chest: "Common Weapon Chest",
    common_armor_chest: "Common Armor Chest",
    uncommon_weapon_chest: "Uncommon Weapon Chest",
    uncommon_armor_chest: "Uncommon Armor Chest",
    rare_weapon_chest: "Rare Weapon Chest",
    rare_armor_chest: "Rare Armor Chest",
  };
  return {
    kind: "image",
    src: CONSUMABLE_ASSETS[consumableId] || "/campaign-stamina.png",
    alt: consumableId.replaceAll("_", " "),
    shortLabel: amount > 1 ? `${amount}× ${shortLabels[consumableId] || "Item"}` : (shortLabels[consumableId] || "Item"),
  };
}

function RewardBadge({ entry, testId, variant = "full" }) {
  const visual = rewardVisual(entry);
  if (variant === "wheel" || variant === "wheel-tiny") {
    const isTiny = variant === "wheel-tiny";
    return (
      <div data-testid={testId} className="px-0.5 text-center">
        <div
          className={`font-cinzel font-bold leading-tight text-white ${isTiny ? "text-[9px]" : "text-[11px]"}`}
          style={{ textShadow: "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 2px 4px rgba(0,0,0,0.5)" }}
        >
          {isTiny ? tinyWheelLabel(entry) : visual.shortLabel}
        </div>
      </div>
    );
  }
  const compact = variant === "compact";
  return (
    <div data-testid={testId} className={`flex items-center ${compact ? "justify-center gap-1.5" : "gap-3"}`}>
      {visual.kind === "swatch" ? (
        <div
          className={`${compact ? "h-7 w-7" : "h-10 w-10"} rounded-sm ring-1 ring-white/25 shrink-0 shadow-[0_0_8px_rgba(0,0,0,0.35)]`}
          style={{ backgroundColor: visual.color }}
        />
      ) : (
        <img src={visual.src} alt={visual.alt} className={`${compact ? "h-7 w-7" : "h-10 w-10"} object-contain shrink-0 drop-shadow-[0_0_8px_rgba(0,0,0,0.35)]`} />
      )}
      <div className={compact ? "text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-50" : "min-w-0"}>
        {compact ? visual.shortLabel : (
          <>
            <div className="font-cinzel text-base text-zinc-100">{entry.label || historyLabel(entry)}</div>
            <div className="mt-0.5 text-xs uppercase tracking-[0.16em] text-zinc-500">{visual.shortLabel}</div>
          </>
        )}
      </div>
    </div>
  );
}

export const WheelOfFortunaModal = ({ open, onClose, summary, onRewardClaimed }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [bigWin, setBigWin] = useState(false);
  const [playerPage, setPlayerPage] = useState(1);
  const [serverPage, setServerPage] = useState(1);
  const wheelRef = useRef(null);
  const settleDelay = 4200;

  const fireConfetti = (big = false) => {
    const rect = wheelRef.current?.getBoundingClientRect();
    const origin = rect
      ? { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + rect.height / 2) / window.innerHeight }
      : { x: 0.5, y: 0.4 };
    const colors = big
      ? ["#fbbf24", "#f59e0b", "#ffffff", "#a855f7", "#f97316", "#fde68a"]
      : ["#fbbf24", "#f59e0b", "#fde68a", "#ffffff", "#c2410c"];
    confetti({ particleCount: big ? 160 : 90, spread: big ? 100 : 75, startVelocity: big ? 55 : 42, gravity: 0.9, origin, colors, scalar: big ? 1.15 : 0.9, zIndex: 9999 });
    window.setTimeout(() => {
      confetti({ particleCount: big ? 90 : 50, spread: big ? 130 : 100, startVelocity: big ? 40 : 30, gravity: 0.9, origin, colors, scalar: big ? 0.9 : 0.75, zIndex: 9999 });
    }, 180);
    if (big) {
      window.setTimeout(() => {
        confetti({ particleCount: 70, angle: 60, spread: 60, startVelocity: 50, origin: { x: 0, y: 0.65 }, colors, zIndex: 9999 });
        confetti({ particleCount: 70, angle: 120, spread: 60, startVelocity: 50, origin: { x: 1, y: 0.65 }, colors, zIndex: 9999 });
      }, 280);
      window.setTimeout(() => {
        confetti({ particleCount: 60, spread: 360, startVelocity: 35, origin, colors, scalar: 1.1, ticks: 260, zIndex: 9999 });
      }, 520);
    }
  };

  const load = async (nextPlayerPage = playerPage, nextServerPage = serverPage) => {
    setLoading(true);
    try {
      const response = await api.get("/daily-ludus/fortuna", { params: { player_page: nextPlayerPage, server_page: nextServerPage } });
      setData(response.data);
      setPlayerPage(nextPlayerPage);
      setServerPage(nextServerPage);
    } catch (error) {
      toast.error(apiErr(error, "Could not load Wheel of Fortuna"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setBigWin(false);
    load(1, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const entries = useMemo(() => data?.entries || summary?.entries || [], [data?.entries, summary?.entries]);
  const slices = useMemo(() => buildSliceGeometry(entries), [entries]);

  const spin = async () => {
    setSpinning(true);
    setResult(null);
    setBigWin(false);
    try {
      const response = await api.post("/daily-ludus/fortuna/spin");
      const payload = response.data;
      const nextEntries = payload.entries || entries;
      const nextSlices = buildSliceGeometry(nextEntries);
      const selectedIndex = nextSlices.findIndex((entry) => entry.id === payload.selected_entry_id);
      const selected = nextSlices[Math.max(0, selectedIndex)];
      const wantMod = (((360 - (selected?.mid || 0)) % 360) + 360) % 360;
      const currentMod = ((rotation % 360) + 360) % 360;
      const delta = ((wantMod - currentMod) % 360 + 360) % 360;
      const targetRotation = rotation + (360 * 6) + delta;
      setRotation(targetRotation);

      const totalWeight = nextEntries.reduce((sum, entry) => sum + Number(entry.weight || 0), 0) || 1;
      const weightFraction = Number(selected?.weight || 0) / totalWeight;
      const rewardRarity = String(payload.reward?.fragment_rarity || "").toLowerCase();
      const isBigWin = rewardRarity === "legendary" || rewardRarity === "elite" || weightFraction <= 0.05;

      window.setTimeout(async () => {
        setResult(payload.reward);
        setBigWin(isBigWin);
        fireConfetti(isBigWin);
        setData((current) => current ? {
          ...current,
          entries: payload.entries || current.entries,
          status: payload.status,
          player_history: payload.player_history,
          server_history: payload.server_history,
        } : payload);
        const rewardType = String(payload.reward?.type || "").toLowerCase();
        const rewardToast = ["gold", "follis"].includes(rewardType) ? currencyGainToast : toast.success;
        rewardToast(`Fortuna smiles — ${payload.reward.label}`);
        await onRewardClaimed?.();
        setSpinning(false);
      }, settleDelay);
    } catch (error) {
      toast.error(apiErr(error, "Wheel spin failed"));
      setSpinning(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={spinning ? undefined : onClose} title="Wheel of Fortuna" wide>
      {loading && !data ? <Loader label="Summoning Fortuna…" /> : !data ? <Empty label="The wheel could not be loaded right now." /> : (
        <div data-testid="wheel-of-fortuna-modal" className="space-y-6">
          <section data-testid="wheel-of-fortuna-status" className="rounded-sm border border-amber-700/35 bg-[radial-gradient(circle_at_top_left,rgba(251,191,36,0.16),transparent_30%),linear-gradient(135deg,rgba(24,24,27,0.96),rgba(9,9,11,0.98))] p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-amber-300">Daily reward activity</div>
                <h2 data-testid="wheel-of-fortuna-heading" className="mt-2 font-cinzel text-3xl text-zinc-50">One free spin each server day.</h2>
                <p data-testid="wheel-of-fortuna-status-copy" className="mt-2 max-w-2xl text-sm text-zinc-300">
                  {data.status?.admin_test_mode
                    ? "Admin test mode is active here, so you can spin repeatedly to verify animation, rewards, and slice tuning without waiting for reset."
                    : "Read the slices, press Spin, and collect one immediate progression reward when the wheel stops."}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:w-[28rem]">
                <div data-testid="wheel-of-fortuna-ready-card" className="rounded-sm border border-white/10 bg-black/20 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Free spin</div>
                  <div className="mt-2 font-cinzel text-2xl text-amber-300">{data.status?.admin_test_mode ? "Admin test mode" : data.status?.free_spin_ready ? "Ready now" : "Spent today"}</div>
                </div>
                <div data-testid="wheel-of-fortuna-next-card" className="rounded-sm border border-white/10 bg-black/20 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Next spin</div>
                  <div className="mt-2 text-sm text-zinc-100">{data.status?.free_spin_ready ? "Available immediately" : formatDateTime(data.status?.next_spin_at)}</div>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr] xl:items-start">
            <div className="space-y-4">
              <div ref={wheelRef} className="relative mx-auto aspect-square max-w-[26rem]">
                {spinning && <div data-testid="wheel-of-fortuna-spin-glow" className="absolute inset-6 rounded-full border border-amber-400/30 bg-amber-400/10 blur-2xl animate-pulse" />}
                {result && (
                  <div
                    data-testid="wheel-of-fortuna-result-flare"
                    className={`absolute inset-8 rounded-full border-2 animate-pulse ${bigWin ? "border-amber-400/50 shadow-[0_0_70px_rgba(251,191,36,0.55)]" : "border-emerald-400/35 shadow-[0_0_50px_rgba(52,211,153,0.4)]"}`}
                  />
                )}
                <div className="absolute left-1/2 -top-1 z-10 h-0 w-0 -translate-x-1/2 border-x-[18px] border-t-[28px] border-x-transparent border-t-amber-300 drop-shadow-[0_0_10px_rgba(251,191,36,0.7)]" />
                <div
                  data-testid="wheel-of-fortuna-wheel"
                  className="absolute inset-0 rounded-full border-[6px] border-[#3b2410] shadow-[0_0_30px_rgba(251,191,36,0.18),inset_0_0_0_2px_rgba(251,191,36,0.35)] transition-transform pointer-events-none"
                  style={{
                    background: wheelGradient(entries),
                    transform: `rotate(${rotation}deg)`,
                    transitionDuration: `${settleDelay}ms`,
                    transitionTimingFunction: "cubic-bezier(0.12,0.9,0.22,1)",
                  }}
                >
                  {slices.map((slice) => (
                    <div key={`sep-${slice.id}`} className="absolute inset-0" style={{ transform: `rotate(${slice.start}deg)` }}>
                      <div className="absolute left-1/2 top-0 h-1/2 w-px -translate-x-1/2 bg-black/25" />
                    </div>
                  ))}
                  {slices.map((slice) => {
                    const tiny = slice.sweep < 18;
                    return (
                      <div key={slice.id} className="absolute inset-0" style={{ transform: `rotate(${slice.mid}deg)` }}>
                        <div
                          data-testid={`wheel-slice-label-${slice.id}`}
                          title={slice.label}
                          className={`absolute left-1/2 -translate-x-1/2 ${tiny ? "w-14" : "w-20"}`}
                          style={{ top: tiny ? "5%" : "15%" }}
                        >
                          <div style={{ transform: `rotate(${-slice.mid}deg)` }}>
                            <RewardBadge entry={slice} testId={`wheel-slice-reward-${slice.id}`} variant={tiny ? "wheel-tiny" : "wheel"} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className={`absolute inset-[32%] rounded-full border border-white/10 bg-zinc-950/90 backdrop-blur-sm flex flex-col items-center justify-center text-center px-4 ${spinning ? "shadow-[0_0_28px_rgba(251,191,36,0.22)]" : ""}`}>
                    <img
                      data-testid="wheel-of-fortuna-center-coin"
                      src="/gold-coin.png"
                      alt="Gold coin"
                      className={`h-8 w-8 object-contain drop-shadow-[0_0_12px_rgba(251,191,36,0.45)] ${spinning ? "animate-pulse" : ""}`}
                    />
                    <div className="mt-2 font-cinzel text-lg text-zinc-50">Fortuna</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-zinc-500">Prize wheel</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  data-testid="wheel-of-fortuna-spin-button"
                  type="button"
                  onClick={spin}
                  disabled={!data.status?.can_spin || spinning}
                  className={btn("primary")}
                >
                  {spinning ? "Spinning…" : data.status?.admin_test_mode ? "Spin Test Wheel" : data.status?.can_spin ? "Spin the Wheel" : "Spin on Cooldown"}
                </button>
                <div data-testid="wheel-of-fortuna-reset-text" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500">
                  {data.status?.admin_test_mode ? `Free-spin reset ${formatDateTime(data.status?.next_spin_at)}` : `Next reset ${formatDateTime(data.status?.next_spin_at)}`}
                </div>
              </div>

              {result && (
                <div
                  data-testid="wheel-of-fortuna-result"
                  className={`rounded-sm border p-4 text-center ${bigWin
                    ? "border-amber-500/50 bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.22),transparent_42%),linear-gradient(180deg,rgba(120,53,15,0.32),rgba(9,9,11,0.92))] shadow-[0_0_40px_rgba(251,191,36,0.28)]"
                    : "border-emerald-700/35 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.18),transparent_42%),linear-gradient(180deg,rgba(6,78,59,0.32),rgba(9,9,11,0.92))] shadow-[0_0_30px_rgba(16,185,129,0.18)]"}`}
                >
                  {bigWin && <div data-testid="wheel-of-fortuna-top-prize-tag" className="mb-2 inline-flex items-center rounded-full border border-amber-400/50 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">Top Prize</div>}
                  <div className={`text-[11px] uppercase tracking-[0.2em] ${bigWin ? "text-amber-300" : "text-emerald-300"}`}>Winning result</div>
                  <div className="mt-4 flex justify-center">
                    <RewardBadge entry={result} testId="wheel-of-fortuna-result-reward" />
                  </div>
                  <div className="mt-3 text-sm text-zinc-300">{data.status?.admin_test_mode ? "Admin test reward granted immediately for wheel validation." : "The reward has been granted to your account immediately."}</div>
                </div>
              )}
            </div>

            <div className="space-y-5">
              <div data-testid="wheel-of-fortuna-prizes" className="rounded-sm border border-amber-900/35 bg-zinc-950/70 p-4">
                <div className="text-[11px] uppercase tracking-[0.16em] text-amber-300">Visible prizes</div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {entries.map((entry) => (
                    <div key={entry.id} data-testid={`wheel-prize-row-${entry.id}`} className="rounded-sm border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-sm text-zinc-200">
                      <RewardBadge entry={entry} testId={`wheel-prize-badge-${entry.id}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div data-testid="wheel-of-fortuna-player-history" className="rounded-sm border border-amber-900/35 bg-zinc-950/70 p-4">
                <div className="font-cinzel text-xl text-amber-200">Your 5 Most Recent Wins</div>
                <div className="mt-3 space-y-2">
                  {(data.player_history?.items || []).length === 0 ? <Empty label="No Fortuna wins yet. Your first spin will start the ledger." /> : (
                    data.player_history.items.map((row) => (
                      <div key={row.id} data-testid={`wheel-player-history-row-${row.id}`} className="rounded-sm border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                        <RewardBadge entry={row.reward_payload || row} testId={`wheel-player-history-badge-${row.id}`} />
                        <div className="mt-2 text-xs text-zinc-500">{formatDateTime(row.created_at)}</div>
                      </div>
                    ))
                  )}
                </div>
                <Pagination page={playerPage} totalPages={data.player_history?.total_pages || 1} onChange={(page) => load(page, serverPage)} testid="wheel-player-history-pagination" className="mt-4" />
              </div>

              <div data-testid="wheel-of-fortuna-server-history" className="rounded-sm border border-amber-900/35 bg-zinc-950/70 p-4">
                <div className="font-cinzel text-xl text-amber-200">Server Recent Wins</div>
                <div className="mt-3 space-y-2">
                  {(data.server_history?.items || []).length === 0 ? <Empty label="No server wins have been recorded yet." /> : (
                    data.server_history.items.map((row) => (
                      <div key={row.id} data-testid={`wheel-server-history-row-${row.id}`} className="rounded-sm border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                        <RewardBadge entry={row.reward_payload || row} testId={`wheel-server-history-badge-${row.id}`} />
                        <div className="mt-2 text-xs text-zinc-500">{row.user_name} · {formatDateTime(row.created_at)}</div>
                      </div>
                    ))
                  )}
                </div>
                <Pagination page={serverPage} totalPages={data.server_history?.total_pages || 1} onChange={(page) => load(playerPage, page)} testid="wheel-server-history-pagination" className="mt-4" />
              </div>
            </div>
          </section>
        </div>
      )}
    </Modal>
  );
};